import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCrmContacts,
  createCrmContact,
  updateCrmContact,
  deleteCrmContact,
  fetchCrmDeals,
  fetchCrmPipelines,
} from "../features/crm/crmThunks.js";

import { MessageCircle, Pencil, Trash2, X } from "lucide-react";
import CrmWhatsAppPanel from "./CrmWhatsAppPanel.jsx";

import "../styles/crm/crmcontacts.css";
import "../styles/crm/crmwhatsapp.css";

const getContactTypeLabel = (type) => {
  if (type === "lead") return "Lead";
  if (type === "patient") return "Paciente";
  if (type === "other") return "Otro";
  return type || "-";
};

const getSourceLabel = (source) => {
  if (source === "web") return "Web";
  if (source === "instagram") return "Instagram";
  if (source === "referral") return "Recomendación";
  if (source === "other") return "Otro";
  return source || "-";
};

const pickMainDeal = (deals) => {
  if (!Array.isArray(deals) || deals.length === 0) return null;
  return deals.find((deal) => deal.status === "open") || deals[0];
};

const getInitials = (fullName) => {
  const cleanName = String(fullName || "").trim();
  if (!cleanName) return "—";
  const parts = cleanName.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const second = parts.length > 1 ? parts[1]?.[0] : parts[0]?.[1] || "";
  return (first + second).toUpperCase();
};

const CrmContacts = () => {
  const dispatch = useDispatch();

  const crmState = useSelector((state) => state.crm || {});
  const contacts = crmState.contacts || [];
  const deals = crmState.deals || [];
  const pipelines = crmState.pipelines || [];
  const loading = crmState.loading;
  const error = crmState.error;

  const [contactTypeFilter, setContactTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingContactId, setEditingContactId] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    contactType: "lead",
    source: "other",
    notes: "",
  });

  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [selectedWhatsAppContact, setSelectedWhatsAppContact] = useState(null);
  const [selectedWhatsAppDeal, setSelectedWhatsAppDeal] = useState(null);

  // Modal eliminar (confirm)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    dispatch(fetchCrmContacts({}));
    dispatch(fetchCrmPipelines(true));
    dispatch(fetchCrmDeals({}));
  }, [dispatch]);

  useEffect(() => {
    if (!isWhatsAppModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsWhatsAppModalOpen(false);
        setSelectedWhatsAppContact(null);
        setSelectedWhatsAppDeal(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isWhatsAppModalOpen]);

  // UX: cerrar modal eliminar con ESC + bloquear scroll
  useEffect(() => {
    if (!deleteOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      closeDeleteModal();
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteOpen]);

  const dealsByContactId = useMemo(() => {
    const map = {};
    deals.forEach((deal) => {
      const key = String(deal.contact);
      if (!map[key]) map[key] = [];
      map[key].push(deal);
    });
    return map;
  }, [deals]);

  const pipelinesById = useMemo(() => {
    const map = {};
    pipelines.forEach((pipeline) => {
      map[String(pipeline.id)] = pipeline;
    });
    return map;
  }, [pipelines]);

  const handleFilterChange = (type) => {
    setContactTypeFilter(type);
    dispatch(
      fetchCrmContacts({
        contactType: type === "all" ? undefined : type,
        searchQuery: searchQuery || undefined,
      })
    );
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    dispatch(
      fetchCrmContacts({
        contactType: contactTypeFilter === "all" ? undefined : contactTypeFilter,
        searchQuery: searchQuery || undefined,
      })
    );
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      contactType: "lead",
      source: "other",
      notes: "",
    });
    setFormError(null);
    setIsSubmitting(false);
    setEditingContactId(null);
    setFormMode("create");
  };

  const handleOpenCreateForm = () => {
    resetForm();
    setFormMode("create");
    setShowForm(true);
  };

  const handleOpenEditForm = (contact) => {
    setFormData({
      fullName: contact.full_name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      contactType: contact.contact_type || "lead",
      source: contact.source || "other",
      notes: contact.notes || "",
    });
    setFormError(null);
    setIsSubmitting(false);
    setEditingContactId(contact.id);
    setFormMode("edit");
    setShowForm(true);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    if (!formData.fullName.trim()) {
      setFormError("El nombre es obligatorio.");
      return;
    }

    const payload = {
      full_name: formData.fullName.trim(),
      email: formData.email || "",
      phone: formData.phone || "",
      contact_type: formData.contactType || "lead",
      source: formData.source || "other",
      notes: formData.notes || "",
    };

    setIsSubmitting(true);

    if (formMode === "create") {
      const resultAction = await dispatch(createCrmContact(payload));
      if (createCrmContact.fulfilled.match(resultAction)) {
        resetForm();
        setShowForm(false);
      } else {
        const err = resultAction.payload || { detail: "No se pudo crear el contacto." };
        setFormError(err.detail || "No se pudo crear el contacto.");
      }
    } else if (formMode === "edit" && editingContactId) {
      const resultAction = await dispatch(
        updateCrmContact({
          contactId: editingContactId,
          contactData: payload,
        })
      );

      if (updateCrmContact.fulfilled.match(resultAction)) {
        resetForm();
        setShowForm(false);
      } else {
        const err = resultAction.payload || { detail: "No se pudo actualizar el contacto." };
        setFormError(err.detail || "No se pudo actualizar el contacto.");
      }
    }

    setIsSubmitting(false);
  };

  const handleOpenWhatsApp = (contact) => {
    const contactDeals = dealsByContactId[String(contact.id)] || [];
    const mainDeal = pickMainDeal(contactDeals);

    setSelectedWhatsAppContact(contact);
    setSelectedWhatsAppDeal(mainDeal);
    setIsWhatsAppModalOpen(true);
  };

  const handleCloseWhatsApp = () => {
    setIsWhatsAppModalOpen(false);
    setSelectedWhatsAppContact(null);
    setSelectedWhatsAppDeal(null);
  };

  // ---------- DELETE MODAL ----------
  const openDeleteModal = (contact) => {
    setDeleteError(null);
    setContactToDelete(contact);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setContactToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!contactToDelete?.id) return;

    setDeleteError(null);
    setDeleting(true);

    const resultAction = await dispatch(deleteCrmContact(contactToDelete.id));

    setDeleting(false);

    if (deleteCrmContact.fulfilled.match(resultAction)) {
      closeDeleteModal();
      return;
    }

    const err = resultAction.payload || { detail: "No se pudo eliminar el contacto." };
    setDeleteError(err.detail || "No se pudo eliminar el contacto.");
  };

  if (loading && contacts.length === 0) {
    return <div className="crm-contacts-loading">Cargando contactos...</div>;
  }

  if (error && contacts.length === 0) {
    return (
      <div className="crm-contacts-error">
        Error al cargar contactos: {error.detail || "Error"}
      </div>
    );
  }

  return (
    <div className="crm-contacts-container">
      <div className="crm-contacts-header">
        <div className="crm-contacts-title">
          <h2>Contactos</h2>
          <p className="crm-contacts-subtitle">Leads y pacientes potenciales de la clínica.</p>
          <div className="crm-contacts-count">{contacts.length} contactos</div>
        </div>

        <div className="crm-contacts-actions">
          <form className="crm-contacts-search-form" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Buscar por nombre…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <button type="submit" className="btn-primary btn-xs">
              Buscar
            </button>
          </form>

          <div className="crm-contacts-filters segmented">
            <button
              type="button"
              className={`segmented-btn ${contactTypeFilter === "all" ? "is-active" : ""}`}
              onClick={() => handleFilterChange("all")}
            >
              Todos
            </button>
            <button
              type="button"
              className={`segmented-btn ${contactTypeFilter === "lead" ? "is-active" : ""}`}
              onClick={() => handleFilterChange("lead")}
            >
              Leads
            </button>
            <button
              type="button"
              className={`segmented-btn ${contactTypeFilter === "patient" ? "is-active" : ""}`}
              onClick={() => handleFilterChange("patient")}
            >
              Pacientes
            </button>
          </div>

          <button type="button" className="btn-primary btn-sm" onClick={handleOpenCreateForm}>
            Nuevo contacto
          </button>

          {loading ? <div className="crm-contacts-inline-status">Actualizando…</div> : null}
        </div>
      </div>

      {showForm ? (
        <div className="crm-contact-form-wrapper">
          <form className="crm-contact-form" onSubmit={handleFormSubmit}>
            <h3>{formMode === "create" ? "Nuevo contacto" : "Editar contacto"}</h3>

            <div className="crm-contact-form-row">
              <div className="crm-contact-field">
                <label htmlFor="fullName">Nombre completo</label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleFormChange}
                  placeholder="Ej: Laura Gómez"
                />
              </div>

              <div className="crm-contact-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Ej: laura@example.com"
                />
              </div>

              <div className="crm-contact-field">
                <label htmlFor="phone">Teléfono</label>
                <input
                  id="phone"
                  name="phone"
                  type="text"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder="Ej: +34 600 000 000"
                />
              </div>
            </div>

            <div className="crm-contact-form-row">
              <div className="crm-contact-field">
                <label htmlFor="contactType">Tipo</label>
                <select
                  id="contactType"
                  name="contactType"
                  value={formData.contactType}
                  onChange={handleFormChange}
                >
                  <option value="lead">Lead</option>
                  <option value="patient">Paciente</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              <div className="crm-contact-field">
                <label htmlFor="source">Origen</label>
                <select id="source" name="source" value={formData.source} onChange={handleFormChange}>
                  <option value="web">Web</option>
                  <option value="instagram">Instagram</option>
                  <option value="referral">Recomendación</option>
                  <option value="other">Otro</option>
                </select>
              </div>
            </div>

            <div className="crm-contact-form-row">
              <div className="crm-contact-field crm-contact-field-notes">
                <label htmlFor="notes">Notas</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows="3"
                  value={formData.notes}
                  onChange={handleFormChange}
                  placeholder="Notas rápidas sobre el contacto…"
                />
              </div>
            </div>

            {formError ? <div className="crm-contact-form-error">{formError}</div> : null}

            <div className="crm-contact-form-actions">
              <button type="submit" className="btn-primary btn-sm" disabled={isSubmitting}>
                {isSubmitting
                  ? "Guardando..."
                  : formMode === "create"
                  ? "Crear contacto"
                  : "Guardar cambios"}
              </button>

              <button
                type="button"
                className="ghost btn-sm"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {contacts.length === 0 ? (
        <p className="crm-contacts-empty">No hay contactos registrados.</p>
      ) : (
        <div className="crm-contacts-table" role="table" aria-label="Contactos CRM">
          <div className="crm-contacts-row crm-contacts-row-head" role="row">
            <div className="crm-contacts-cell is-contact" role="columnheader">
              Contacto
            </div>
            <div className="crm-contacts-cell is-pipeline" role="columnheader">
              Pipeline
            </div>
            <div className="crm-contacts-cell is-actions" role="columnheader">
              Acciones
            </div>
          </div>

          {contacts.map((contact) => {
            const contactDeals = dealsByContactId[String(contact.id)] || [];
            const mainDeal = pickMainDeal(contactDeals);

            const pipeline = mainDeal ? pipelinesById[String(mainDeal.pipeline)] : null;
            const stages = pipeline?.stages
              ? pipeline.stages.slice().sort((a, b) => a.order - b.order)
              : [];

            const currentStage = mainDeal
              ? stages.find((stage) => String(stage.id) === String(mainDeal.stage))
              : null;

            const currentStageOrder = currentStage?.order ?? null;
            const currentStageName = currentStage?.name ?? null;
            const dealStatus = mainDeal?.status ?? null;

            const funnelClassName = [
              "crm-contact-funnel",
              dealStatus === "won" ? "is-closed-won" : "",
              dealStatus === "lost" ? "is-closed-lost" : "",
              dealStatus === "cancelled" ? "is-cancelled" : "",
            ]
              .join(" ")
              .trim();

            const contactEmail = contact.email || "";
            const contactPhone = contact.phone || "";

            return (
              <div key={contact.id} className="crm-contacts-row" role="row">
                <div className="crm-contacts-cell is-contact" role="cell">
                  <div className="crm-contact-card">
                    <div className="crm-contact-avatar" aria-hidden="true">
                      {getInitials(contact.full_name)}
                    </div>

                    <div className="crm-contact-info">
                      <div className="crm-contact-name" title={contact.full_name || ""}>
                        {contact.full_name}
                      </div>

                      <div className="crm-contact-secondary">
                        <span className="crm-contact-secondary-item" title={contactEmail}>
                          {contactEmail || "—"}
                        </span>

                        {contactEmail && contactPhone ? <span className="crm-dot-sep">·</span> : null}

                        <span className="crm-contact-secondary-item" title={contactPhone}>
                          {contactPhone || "—"}
                        </span>
                      </div>

                      <div className="crm-contact-tags">
                        <span className="crm-tag">{getContactTypeLabel(contact.contact_type)}</span>
                        <span className="crm-tag is-soft">{getSourceLabel(contact.source)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="crm-contacts-cell is-pipeline" role="cell">
                  {mainDeal && stages.length > 0 && currentStage ? (
                    <div className={funnelClassName}>
                      <div className="crm-contact-funnel-line" aria-hidden="true">
                        {stages.map((stage) => {
                          const isPast = currentStageOrder !== null && stage.order < currentStageOrder;
                          const isCurrent = String(stage.id) === String(mainDeal.stage);

                          return (
                            <div
                              key={stage.id}
                              className={
                                "crm-contact-funnel-step" +
                                (isPast ? " is-past" : "") +
                                (isCurrent ? " is-current" : "")
                              }
                              title={stage.name}
                            >
                              <span className="crm-contact-funnel-dot" />
                            </div>
                          );
                        })}
                      </div>

                      <div className="crm-contact-funnel-label" title={currentStageName || ""}>
                        {dealStatus === "won"
                          ? `Ganado – ${currentStageName || ""}`
                          : dealStatus === "lost"
                          ? `Perdido – ${currentStageName || ""}`
                          : dealStatus === "cancelled"
                          ? `Cancelado – ${currentStageName || ""}`
                          : currentStageName || "En pipeline"}
                      </div>
                    </div>
                  ) : (
                    <span className="crm-contact-funnel-empty">Sin casos</span>
                  )}
                </div>

                <div className="crm-contacts-cell is-actions" role="cell">
                  <div className="crm-contacts-action-icons">
                    <button
                      type="button"
                      className="crm-icon-btn"
                      onClick={() => handleOpenWhatsApp(contact)}
                      title="WhatsApp"
                      aria-label="Abrir WhatsApp"
                    >
                      <MessageCircle size={18} />
                    </button>

                    <button
                      type="button"
                      className="crm-icon-btn"
                      onClick={() => handleOpenEditForm(contact)}
                      title="Editar"
                      aria-label="Editar contacto"
                    >
                      <Pencil size={18} />
                    </button>

                    <button
                      type="button"
                      className="crm-icon-btn danger"
                      onClick={() => openDeleteModal(contact)}
                      title="Eliminar"
                      aria-label="Eliminar contacto"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isWhatsAppModalOpen && selectedWhatsAppContact ? (
        <div className="crm-wa-modal-overlay" onMouseDown={handleCloseWhatsApp} role="presentation">
          <div
            className="crm-wa-modal"
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="crm-wa-modal-body">
              <CrmWhatsAppPanel
                contactId={selectedWhatsAppContact.id}
                dealId={selectedWhatsAppDeal ? selectedWhatsAppDeal.id : null}
                defaultPhone={selectedWhatsAppContact.phone || ""}
                pipelineId={selectedWhatsAppDeal ? selectedWhatsAppDeal.pipeline : null}
                stageId={selectedWhatsAppDeal ? selectedWhatsAppDeal.stage : null}
                onClose={handleCloseWhatsApp}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL ELIMINAR */}
      {deleteOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar contacto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar contacto</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDeleteModal}
                disabled={deleting}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar{" "}
                <strong>{contactToDelete?.full_name || `Contacto #${contactToDelete?.id}`}</strong>?
                <div className="ui-delete-subhelp">Esta acción no se puede deshacer.</div>
              </div>

              {deleteError ? <div className="ui-modal-error">{deleteError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmContacts;